import React, { useEffect, useState } from 'react';
import {
  Calendar,
  CheckCircle2,
  Clock,
  ExternalLink,
  Image,
  MessageSquare,
  RotateCcw,
  User,
  X,
  XCircle
} from 'lucide-react';
import { useNotification } from '../common/Notification';
import { useAuth } from '../../contexts/AuthContext';
import { UserService } from '../../services/userService';
import { TopupOrderService } from '../../services/topupOrderService';
import TopupOrderConversation from './TopupOrderConversation';

const formatCurrency = (amount) => `${Number(amount || 0).toLocaleString('fr-FR')} DA`;

const formatDate = (value) => {
  if (!value) return 'Non defini';
  const date = value?.toDate ? value.toDate() : new Date(value);
  return date.toLocaleString('fr-FR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const resolveStatus = (transaction) => {
  if (transaction?.type === 'topup_refund') return 'refunded';
  return transaction?.status || transaction?.topupStatus || 'pending';
};

const getStatusMeta = (status) => {
  if (status === 'completed') {
    return {
      label: 'Validee',
      classes: 'border-emerald-200 bg-emerald-50 text-emerald-700',
      icon: <CheckCircle2 className="h-5 w-5 text-emerald-600" />
    };
  }

  if (status === 'refused') {
    return {
      label: 'Refusee',
      classes: 'border-red-200 bg-red-50 text-red-700',
      icon: <XCircle className="h-5 w-5 text-red-600" />
    };
  }

  if (status === 'refunded') {
    return {
      label: 'Remboursee',
      classes: 'border-blue-200 bg-blue-50 text-blue-700',
      icon: <RotateCcw className="h-5 w-5 text-blue-600" />
    };
  }

  if (status === 'in_progress') {
    return {
      label: 'En cours',
      classes: 'border-blue-200 bg-blue-50 text-blue-700',
      icon: <Clock className="h-5 w-5 text-blue-600" />
    };
  }

  return {
    label: 'En attente',
    classes: 'border-amber-200 bg-amber-50 text-amber-700',
    icon: <Clock className="h-5 w-5 text-amber-600" />
  };
};

const TopupTransactionModal = ({ transaction, isOpen, onClose }) => {
  const { addNotification } = useNotification();
  const { currentUser, userProfile } = useAuth();
  const [adminName, setAdminName] = useState('');
  const [orderDetails, setOrderDetails] = useState(null);
  const [orderLoading, setOrderLoading] = useState(false);

  useEffect(() => {
    const fetchAdminName = async () => {
      const processedBy = orderDetails?.processedBy || transaction?.processedBy;

      if (!processedBy) {
        setAdminName('');
        return;
      }

      try {
        const name = await UserService.getAdminName(processedBy);
        setAdminName(name);
      } catch (error) {
        console.error('Erreur lors de la recuperation du nom admin Topup:', error);
        setAdminName('Administrateur');
      }
    };

    if (isOpen && (orderDetails?.processedBy || transaction?.processedBy)) {
      fetchAdminName();
    } else {
      setAdminName('');
    }
  }, [isOpen, orderDetails?.processedBy, transaction?.processedBy]);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      const orderId = String(transaction?.topupOrderId || transaction?.id || '').trim();

      if (!isOpen || !orderId) {
        setOrderDetails(null);
        setOrderLoading(false);
        return;
      }

      try {
        setOrderLoading(true);
        const nextOrder = await TopupOrderService.getOrderById(orderId);
        setOrderDetails(nextOrder);
      } catch (error) {
        console.error('Erreur lors du chargement du detail de commande Topup:', error);
        setOrderDetails(null);
      } finally {
        setOrderLoading(false);
      }
    };

    fetchOrderDetails();
  }, [isOpen, transaction?.id, transaction?.topupOrderId]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  if (!isOpen || !transaction) return null;

  const detailSource = orderDetails || transaction;
  const conversationOrder = {
    ...detailSource,
    id: transaction.topupOrderId || detailSource.id || transaction.id
  };
  const status = resolveStatus(detailSource);
  const statusMeta = getStatusMeta(status);
  const adminNote = detailSource.adminNote || transaction.note || transaction.adminNote || '';
  const proofImageUrl = detailSource.proofImageUrl || transaction.proofImageUrl || '';
  const fieldEntries = Array.isArray(detailSource.formFields)
    ? detailSource.formFields
        .map((field) => ({
          id: field.id || field.label || '',
          label: field.label || field.id || 'Information',
          value: String(detailSource.formData?.[field.id] || '').trim()
        }))
        .filter((entry) => entry.value)
    : [];
  const whatsappEntry = fieldEntries.find((entry) => {
    const haystack = `${entry.id} ${entry.label}`.toLowerCase();
    return haystack.includes('whatsapp') || haystack.includes('whatup') || haystack.includes('phone') || haystack.includes('telephone');
  });
  const customerInfoEntries = fieldEntries.filter((entry) => entry !== whatsappEntry);

  return (
    <div className="fixed inset-0 z-[10002] bg-black/50">
      <div className="flex min-h-full items-end justify-center overflow-hidden sm:items-center sm:p-4">
      <div className="flex h-[calc(100dvh-4rem)] w-full max-w-2xl flex-col overflow-hidden rounded-t-[28px] bg-white shadow-xl sm:h-auto sm:max-h-[92dvh] sm:rounded-[28px]">
        <div className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-slate-200 bg-white px-4 py-4 sm:p-6">
          <div className="flex items-center gap-3">
            <div className="rounded-2xl bg-blue-100 p-2.5 text-blue-600 sm:p-3">
              <CheckCircle2 className="h-5 w-5 sm:h-6 sm:w-6" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-slate-900 sm:text-xl">Details de la transaction Topup</h2>
              <p className="truncate text-xs text-slate-500 sm:text-sm">
                {detailSource.productName || transaction.productName || 'Topup'} - {detailSource.offerValue || detailSource.offerName || transaction.offerValue || transaction.offerName || 'Offre'}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-xl p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6 sm:py-6">
          <div className="space-y-4 pb-4 sm:space-y-6">
          <div className="flex justify-center">
            <div className={`flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold ${statusMeta.classes}`}>
              {statusMeta.icon}
              <span>{statusMeta.label}</span>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2 md:gap-5">
            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-400 sm:text-sm sm:font-medium sm:normal-case sm:tracking-normal">Service</label>
                  <p className="mt-1 text-base font-semibold text-slate-900 sm:text-lg">{detailSource.productName || transaction.productName || 'Topup'}</p>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-400 sm:text-sm sm:font-medium sm:normal-case sm:tracking-normal">Offre</label>
                  <p className="mt-1 text-sm text-slate-900 sm:text-base">{detailSource.offerValue || detailSource.offerName || transaction.offerValue || transaction.offerName || 'Offre'}</p>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-400 sm:text-sm sm:font-medium sm:normal-case sm:tracking-normal">Montant</label>
                  <p className="mt-1 text-lg font-bold text-slate-900 sm:text-xl">{formatCurrency(detailSource.price || transaction.amount)}</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-400 sm:text-sm sm:font-medium sm:normal-case sm:tracking-normal">Date de creation</label>
                <div className="mt-1 flex items-center gap-2 text-slate-900">
                  <Calendar className="h-4 w-4" />
                  <span className="min-w-0 break-words text-sm sm:text-base">{formatDate(detailSource.createdAt || transaction.createdAt)}</span>
                </div>
              </div>

              {detailSource.processedAt || transaction.processedAt || transaction.completedAt || transaction.refusedAt ? (
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wide text-slate-400 sm:text-sm sm:font-medium sm:normal-case sm:tracking-normal">Date de traitement</label>
                  <div className="mt-1 flex items-center gap-2 text-slate-900">
                    <Clock className="h-4 w-4" />
                    <span className="min-w-0 break-words text-sm sm:text-base">{formatDate(detailSource.processedAt || transaction.completedAt || transaction.refusedAt || transaction.processedAt)}</span>
                  </div>
                </div>
              ) : null}

              {adminName ? (
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wide text-slate-400 sm:text-sm sm:font-medium sm:normal-case sm:tracking-normal">Traite par</label>
                  <div className="mt-1 flex items-center gap-2 text-slate-900">
                    <User className="h-4 w-4" />
                    <span className="text-sm sm:text-base">{adminName}</span>
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          {orderLoading ? (
            <div className="rounded-2xl bg-slate-50 p-4 text-sm text-slate-500">
              Chargement des informations de commande...
            </div>
          ) : null}

          {whatsappEntry ? (
            <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4">
              <div className="flex items-start gap-2">
                <MessageSquare className="mt-0.5 h-5 w-5 text-blue-600" />
                <div>
                  <label className="text-sm font-medium text-blue-700">WhatsApp du client</label>
                  <p className="mt-1 break-words text-sm font-semibold text-slate-900">{whatsappEntry.value}</p>
                </div>
              </div>
            </div>
          ) : null}

          {customerInfoEntries.length > 0 ? (
            <div className="rounded-2xl bg-slate-50 p-4">
              <div className="mb-3 flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-slate-500" />
                <label className="text-sm font-medium text-slate-700">Informations du client</label>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {customerInfoEntries.map((entry) => (
                  <div key={`${entry.id}-${entry.label}`} className="rounded-2xl border border-slate-200 bg-white p-3">
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{entry.label}</p>
                    <p className="mt-1 break-words text-sm font-semibold text-slate-900">{entry.value}</p>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {adminNote ? (
            <div className="rounded-2xl bg-slate-50 p-4">
              <div className="flex items-start gap-2">
                <MessageSquare className="mt-0.5 h-5 w-5 text-slate-500" />
                <div>
                  <label className="text-sm font-medium text-slate-700">Message de l administrateur</label>
                  <p className="mt-1 text-sm text-slate-900">{adminNote}</p>
                </div>
              </div>
            </div>
          ) : null}

          {status === 'completed' && proofImageUrl ? (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
              <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2">
                  <Image className="h-5 w-5 text-emerald-600" />
                  <div>
                    <label className="text-sm font-medium text-emerald-700">Capture de preuve</label>
                    <p className="text-sm text-emerald-600">Preuve envoyee par l administrateur</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => window.open(proofImageUrl, '_blank', 'noopener,noreferrer')}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700 sm:w-auto"
                >
                  <ExternalLink className="h-4 w-4" />
                  Ouvrir
                </button>
              </div>

              <div className="rounded-2xl border border-emerald-100 bg-white p-4 text-center text-sm text-slate-600">
                La capture ne s affiche pas directement pour des raisons de securite.
              </div>
            </div>
          ) : null}

          <div className="rounded-2xl bg-slate-50 p-4">
            <label className="text-xs font-medium uppercase tracking-wide text-slate-500">Reference</label>
            <p className="mt-1 break-all font-mono text-sm text-slate-700">{transaction.topupOrderId || detailSource.id || transaction.id}</p>
          </div>

          <TopupOrderConversation
            order={conversationOrder}
            currentUser={currentUser}
            userProfile={userProfile}
            addNotification={addNotification}
            isStaff={false}
          />
          </div>
        </div>

        <div className="sticky bottom-0 flex justify-end border-t border-slate-200 bg-white p-4 sm:p-6">
          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-2xl bg-slate-700 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 sm:w-auto"
          >
            Fermer
          </button>
        </div>
      </div>
      </div>
    </div>
  );
};

export default TopupTransactionModal;
